// FILE: attachmentUrls.ts
// Purpose: Build the URLs the browser uses to fetch attachment bytes from the server.
// Layer: Web utility
// Depends on: `resolveServerHttpOrigin` for the server's HTTP origin.

import { resolveServerHttpOrigin } from "./serverOrigin";

/** Route the server serves attachment bytes from (`wsServer.ts`, `GET /attachments/:id`). */
export function attachmentPreviewRoutePath(attachmentId: string): string {
  return `/attachments/${encodeURIComponent(attachmentId)}`;
}

/**
 * Absolutize a preview URL.
 *
 * Server-issued URLs are root-relative, which is wrong for the two clients whose
 * page did not come from the server (Vite dev on :5733, any client pointed at a
 * remote server through `VITE_WS_URL`). Anything already absolute is left alone
 * so data: URLs from an unsent composer attachment pass through untouched.
 */
export function toAttachmentPreviewUrl(rawUrl: string): string {
  if (rawUrl.startsWith("/")) {
    return `${resolveServerHttpOrigin()}${rawUrl}`;
  }
  return rawUrl;
}

/** Absolute URL for an attachment id — what an `<img src>` or a `fetch` needs. */
export function resolveAttachmentPreviewUrl(attachmentId: string): string {
  return toAttachmentPreviewUrl(attachmentPreviewRoutePath(attachmentId));
}

/**
 * Absolute URL for downloading an attachment under its original name.
 *
 * The `name` is what the server puts in `Content-Disposition`. `<a download>`
 * alone is not enough: the browser ignores that attribute when the file comes
 * from another origin, which is every Vite-dev and remote-server client, and
 * the download would then be named after the attachment id.
 */
export function resolveAttachmentDownloadUrl(attachmentId: string, name: string): string {
  const base = attachmentPreviewRoutePath(attachmentId);
  return toAttachmentPreviewUrl(`${base}?name=${encodeURIComponent(name)}`);
}
